import { cn } from "@/lib/utils";

const LOADING_SIZE = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-2",
  lg: "h-10 w-10 border-[3px]",
} as const;

export type LoadingSize = keyof typeof LOADING_SIZE;

export interface LoadingRingProps {
  className?: string;
  size?: LoadingSize;
}

/** Visual-only spinning ring — for buttons, pills, and tight icon slots. */
export const LoadingRing = ({ className, size = "md" }: LoadingRingProps) => (
  <div
    aria-hidden
    className={cn(
      "animate-spin rounded-full border-primary border-t-transparent",
      LOADING_SIZE[size],
      className
    )}
  />
);

export interface LoadingStateProps {
  className?: string;
  /** Primary caption. Hidden for `icon` (sr-only). */
  message?: string;
  /** Secondary line under `message` (`page` / `section` / `overlay`). */
  description?: string;
  size?: LoadingSize;
  /**
   * `page` — full viewport (boot / Suspense)
   * `section` — in-page panel (default)
   * `inline` — horizontal spinner + text
   * `overlay` — absolute/fixed scrim over content
   * `icon` — ring only (accessible via `message` / label)
   */
  variant?: "page" | "section" | "inline" | "overlay" | "icon";
  /** With `overlay`: use `position: fixed` instead of `absolute`. */
  fixed?: boolean;
}

/**
 * Single loading UI for the whole app.
 * Prefer this over ad-hoc spinners. Use `LoadingRing` only for tiny icon slots
 * that must inherit `currentColor` (e.g. Button loading).
 */
export const LoadingState = ({
  className,
  message = "Đang tải…",
  description,
  size,
  variant = "section",
  fixed = false,
}: LoadingStateProps) => {
  const resolvedSize: LoadingSize =
    size ??
    (variant === "inline" || variant === "icon" || variant === "overlay"
      ? "sm"
      : "md");

  if (variant === "icon") {
    return (
      <div
        role="status"
        aria-label={message}
        className={cn("inline-flex shrink-0", className)}
      >
        <LoadingRing size={resolvedSize} />
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 text-sm text-muted-foreground",
          className
        )}
        role="status"
        aria-busy="true"
        aria-label={message}
      >
        <LoadingRing size={resolvedSize} />
        <span>{message}</span>
      </div>
    );
  }

  if (variant === "overlay") {
    return (
      <div
        className={cn(
          "inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-[1px]",
          fixed ? "fixed" : "absolute",
          className
        )}
        role="status"
        aria-busy="true"
        aria-label={message}
      >
        <LoadingRing size={size ?? "md"} />
        {message ? (
          <p className="text-xs text-muted-foreground">{message}</p>
        ) : (
          <span className="sr-only">Đang tải</span>
        )}
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
    );
  }

  const isPage = variant === "page";

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-4 text-center",
        isPage && "h-screen bg-background",
        variant === "section" && "min-h-64 py-10",
        className
      )}
      role="status"
      aria-busy="true"
      aria-label={message}
    >
      <div className="relative flex h-12 w-12 items-center justify-center">
        {isPage ? (
          <>
            <span
              aria-hidden
              className="absolute inset-0 rounded-full border border-primary/15"
            />
            <span
              aria-hidden
              className="absolute inset-1 rounded-full border border-dashed border-primary/25 animate-[spin_3s_linear_infinite]"
            />
          </>
        ) : null}
        <LoadingRing size={resolvedSize} />
      </div>

      <div className="flex flex-col items-center gap-1.5 px-4">
        {message ? (
          <p className="text-sm font-medium text-foreground">{message}</p>
        ) : null}
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
        {isPage ? (
          <div
            className="relative mt-0.5 h-0.5 w-28 overflow-hidden rounded-full bg-muted"
            aria-hidden
          >
            <div className="absolute inset-y-0 w-1/2 rounded-full bg-primary animate-ping-pong" />
          </div>
        ) : null}
      </div>
    </div>
  );
};
